import React from "react";
import { createBaseBlock } from "./BaseBlock";
import { Contributor } from "../components/Contributor";
import { Card, Space, Typography } from "antd";

interface WorkPatternData {
  group: string;
  pattern_summary: string;
  focus_area: string;
  stats: {
    metric: string;
    value: number;
    unit: string;
    evidence: string[];
  }[];
  temporal_patterns: {
    peak_hours: string[];
    peak_days: string[];
    consistency_score: number;
    anomaly_description: string;
    timeseries_evidence: string[];
  };
}

// WorkPatterns Block Recipe
const workPatternsRecipe = {
  id: "work-patterns-block",
  name: "Work Patterns Block",
  globalKey: [],
  prefix: `[
  {
    "group": "Team or activity type",
    "focus_area": "Describe the observed work pattern, e.g., 'mid-day peaks', 'weekend activity', 'low Friday work'. Focus only on patterns by hour or day.",
    "pattern_summary": "Summarize when work happens based on time-of-day and day-of-week, not what was done or by whom. Do not include work volume or task counts.",
    "temporal_patterns": {
      "objective": "Report only if there is actual time-of-day or day-of-week data; otherwise, state 'No significant pattern detected.'",
      "peak_hours": ["string — e.g. '10 AM–12 PM', '9 PM–11 PM'"],
      "peak_days": ["string — e.g. 'Monday', 'Sunday'"],
      "consistency_score": "number — 0 to 1.0 score for regularity in working hours",
      "anomaly_description": "string — e.g. 'Friday is consistently low, Monday spikes in morning activity.'",
      "timeseries_evidence": ["string — summary of time-based work activity, e.g., 'Most activity 2–4 PM weekdays'"]
    }
  }
]`,
  format: {
    type: "json",
    fields: ["pattern_summary", "temporal_patterns"],
    constraints: [
      { field: "pattern_summary", type: "string", required: true },
      { field: "temporal_patterns", type: "object", required: true },
    ],
  },
  validationRules: [
    {
      type: "json_structure",
      condition: (data: any): boolean => {
        try {
          const parsed = JSON.parse(data) as WorkPatternData[];
          return (
            Array.isArray(parsed) &&
            parsed.every(
              (item) => item.pattern_summary && item.temporal_patterns
            )
          );
        } catch {
          return false;
        }
      },
      message: "Response must be valid JSON with required fields",
    },
  ],
};

// WorkPatterns Block Parser
function workPatternsParser(data: any) {
  try {
    return {
      data: data[workPatternsBlock.id],
    };
  } catch (error) {
    return {
      error: true,
      message: `Failed to parse work patterns data: ${
        error instanceof Error ? error.message : String(error)
      }`,
      rawContent: data,
    };
  }
}

// WorkPatterns Block Variants
const workPatternsVariants = [
  {
    id: "default",
    name: "Default",
    description: "Standard work patterns display",
  },
  {
    id: "compact",
    name: "Compact",
    description: "Compact work patterns display",
  },
  {
    id: "minimal",
    name: "Minimal",
    description: "Minimal work patterns display",
  },
];

// WorkPatterns Block Renderer
function WorkPatternsBlockRenderer(props: any) {
  const { data, variant, className, style } = props;
  if (data === undefined) {
    return null;
  }
  const patternsData = data[workPatternsBlock.id];

  if (!patternsData || patternsData.length === 0) return null;

  const renderDefaultCard = (pattern: WorkPatternData) => (
    <Contributor
      name={pattern.group}
      activitySummary={pattern.pattern_summary}
      temporalPatterns={pattern.temporal_patterns}
      stats={pattern.stats}
      className="vzbl-panel-overlay vzbl-panel-shadow"
    />
  );

  const renderCompactCard = (pattern: WorkPatternData) => (
    <Card className="vzbl-panel-overlay vzbl-panel-shadow flex-col-full">
      <Space direction="vertical" size="small">
        <Typography.Text className="vzbl-header-5">
          {pattern.group}
        </Typography.Text>
        <Typography.Text type="secondary">{pattern.focus_area}</Typography.Text>
        <Typography.Text>{pattern.pattern_summary}</Typography.Text>
      </Space>
      <div style={{ marginTop: 8 }}>
        {pattern.stats?.slice(0, 2).map((stat, idx) => (
          <div key={idx}>
            <Typography.Text strong>
              {stat.metric}: {stat.value} {stat.unit}
            </Typography.Text>
          </div>
        ))}
      </div>
    </Card>
  );

  const getCardRenderer = () => {
    switch (variant) {
      case "compact": {
        return renderCompactCard;
      }
      case "minimal": {
        return renderDefaultCard;
      }
      default: {
        return renderDefaultCard;
      }
    }
  };
  const renderCard = getCardRenderer();

  return (
    <div className="vzbl-panel-container">
      <Typography.Title level={4} className="vzbl-header-4">
        Work Patterns
      </Typography.Title>
      <div className="responsive-card-grid">
        {patternsData.map((pattern: WorkPatternData, idx: number) => (
          <div key={`work-pattern-${idx}`} style={{ height: "100%" }}>
            {renderCard(pattern)}
          </div>
        ))}
      </div>
    </div>
  );
}

// Create WorkPatterns Block
const workPatternsBlock = createBaseBlock({
  id: "work-patterns",
  name: "Work Patterns Block",
  description: "Displays work patterns and temporal distributions",
  recipe: workPatternsRecipe,
  variants: workPatternsVariants,
  parser: workPatternsParser,
  renderer: WorkPatternsBlockRenderer,
});

// Export the block for direct use
export { workPatternsBlock };
